import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router";
import { GUIDES, type GuideKey, type GuideStep } from "@/plan-logic/site/guides";
import { markSeen, wasSeen } from "./seen";
import "./guides.css";

/** Delay before a page's first-visit guide opens (the prototype's 360 ms after `nav`). */
const AUTO_DELAY = 360;
/** A `wait:` step moves on this long after the user does the thing (the prototype's 420 ms). */
const WAIT_DELAY = 420;

type GuideApi = {
  /** Runs a page's guide: `force` (the Page guide button) runs it again even when seen. */
  start: (key: GuideKey, force?: boolean) => void;
  /** Whether guides auto-run here: not in tests or the dev gallery unless asked (`?guide`). */
  auto: boolean;
  /** Held while something else has the screen (the check-in). */
  hold: boolean;
  uid?: string;
};

const Ctx = createContext<GuideApi | null>(null);

export function useGuides(): GuideApi | null {
  return useContext(Ctx);
}

/** Set by browser tests against the built app (e2e) so a first-visit guide's veil doesn't block clicks. */
export const GUIDES_OFF_KEY = "abi.guidesOff";

/**
 * Guides run on their own in the app; in tests, the dev gallery, and browsers with `GUIDES_OFF_KEY`
 * set (e2e) only when the address asks (`?guide`), so screenshots and clicks don't meet a veil.
 */
export function guidesAuto(pathname: string, search: string): boolean {
  if (new URLSearchParams(search).has("guide")) return true;
  if (import.meta.env.MODE === "test") return false;
  try {
    if (localStorage.getItem(GUIDES_OFF_KEY) === "1") return false;
  } catch {
    // storage unavailable: the guides run as usual
  }
  return !pathname.startsWith("/dev/");
}

/** The first visible element matching any of `sel` (comma-separated, tried in order). */
export function findAnchor(sel: string | undefined): HTMLElement | null {
  if (!sel) return null;
  for (const one of sel.split(",")) {
    const el = document.querySelector<HTMLElement>(one.trim());
    if (!el) continue;
    const r = el.getBoundingClientRect();
    // jsdom lays nothing out, so a zero box only means "hidden" in a real browser
    if (r.width > 0 || r.height > 0 || import.meta.env.MODE === "test") return el;
  }
  return null;
}

/** The next step from `i` that can be shown: one without an anchor, or whose anchor is on the page. */
function nextShowable(steps: GuideStep[], i: number): number {
  let n = i;
  while (n < steps.length && steps[n].sel && !findAnchor(steps[n].sel)) n++;
  return n;
}

type Active = { key: GuideKey; i: number };

/**
 * Abi's page guides (prototype v45): the provider the shell wraps the app in. Which pages each user
 * has seen is kept in this browser's localStorage, by Firebase uid (no backend yet).
 */
export function GuideProvider({
  uid,
  hold,
  children,
}: {
  uid?: string;
  hold: boolean;
  children: ReactNode;
}) {
  const location = useLocation();
  const auto = guidesAuto(location.pathname, location.search);
  const [active, setActive] = useState<Active | null>(null);

  const start = useCallback(
    (key: GuideKey, force = false) => {
      if (!force && wasSeen(uid, key)) return;
      markSeen(uid, key);
      const i = nextShowable(GUIDES[key], 0);
      setActive(i < GUIDES[key].length ? { key, i } : null);
    },
    [uid],
  );

  // A new page ends the last page's guide; its anchors are gone.
  const path = useRef(location.pathname);
  useEffect(() => {
    if (path.current === location.pathname) return;
    path.current = location.pathname;
    setActive(null);
  }, [location.pathname]);

  const onNext = useCallback(
    () =>
      setActive((a) => {
        if (!a) return a;
        const n = nextShowable(GUIDES[a.key], a.i + 1);
        return n < GUIDES[a.key].length ? { key: a.key, i: n } : null;
      }),
    [],
  );
  const onSkip = useCallback(() => setActive(null), []);

  const api = useMemo(() => ({ start, auto, hold, uid }), [start, auto, hold, uid]);
  return (
    <Ctx.Provider value={api}>
      {children}
      {active && (
        <GuideOverlay
          key={`${active.key}:${active.i}`}
          steps={GUIDES[active.key]}
          i={active.i}
          onNext={onNext}
          onSkip={onSkip}
        />
      )}
    </Ctx.Provider>
  );
}

/**
 * Opens `key`'s guide the first time the page is shown, once its content has rendered (`ready`),
 * and not while the shell holds guides back (the check-in is up).
 */
export function useAutoGuide(key: GuideKey | undefined, ready: boolean) {
  const g = useGuides();
  const enabled = !!g?.auto && !g.hold && ready && !!key;
  const forced = useLocation().search.includes("guide");
  const start = g?.start;
  useEffect(() => {
    if (!enabled || !key || !start) return;
    const t = setTimeout(() => start(key, forced), AUTO_DELAY);
    return () => clearTimeout(t);
  }, [enabled, key, start, forced]);
}

function reducedMotion(): boolean {
  return !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
}

type Dir = "up" | "down" | "left" | "right";
type Place = {
  panel: { left: number; top: number };
  ring?: { left: number; top: number; width: number; height: number };
  arrow?: { left: number; top: number; dir: Dir };
};

/** The prototype's `gdPlace`: below the anchor if it fits, else above, else to the right, else left. */
function place(panel: DOMRect, target: HTMLElement | null): Place {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const pw = panel.width || 360;
  const ph = panel.height || 170;
  if (!target) return { panel: { left: (vw - pw) / 2, top: (vh - ph) / 2 } };
  const r = target.getBoundingClientRect();
  const ring = { left: r.left - 6, top: r.top - 6, width: r.width + 12, height: r.height + 12 };
  const gap = 18;
  let left = Math.min(Math.max(12, r.left + r.width / 2 - pw / 2), vw - pw - 12);
  let top: number;
  let dir: Dir;
  if (r.bottom + gap + ph < vh - 12) {
    top = r.bottom + gap;
    dir = "up";
  } else if (r.top - gap - ph > 12) {
    top = r.top - gap - ph;
    dir = "down";
  } else if (r.right + gap + pw < vw - 12) {
    left = r.right + gap;
    top = Math.min(Math.max(12, r.top), vh - ph - 12);
    dir = "left";
  } else {
    left = Math.max(12, r.left - gap - pw);
    top = Math.min(Math.max(12, r.top), vh - ph - 12);
    dir = "right";
  }
  let arrow: Place["arrow"];
  if (dir === "up" || dir === "down") {
    const ax = Math.min(Math.max(left + 16, r.left + r.width / 2), left + pw - 16);
    arrow = { left: ax - 9, top: dir === "up" ? top - 10 : top + ph, dir };
  } else {
    const ay = Math.min(Math.max(top + 16, r.top + r.height / 2), top + ph - 16);
    arrow = { top: ay - 9, left: dir === "left" ? left - 10 : left + pw, dir };
  }
  return { panel: { left, top }, ring, arrow };
}

const px = (v: number) => `${Math.round(v)}px`;

/** One step: the veil (first step), the ring and arrow (anchored steps), and Abi's panel. */
function GuideOverlay({
  steps,
  i,
  onNext,
  onSkip,
}: {
  steps: GuideStep[];
  i: number;
  onNext: () => void;
  onSkip: () => void;
}) {
  const step = steps[i];
  const last = i === steps.length - 1;
  const [typed, setTyped] = useState(() => (reducedMotion() ? step.t.length : 0));
  const typing = typed < step.t.length;
  const panel = useRef<HTMLDivElement>(null);
  const next = useRef<HTMLButtonElement>(null);
  const target = useMemo(() => findAnchor(step.sel), [step.sel]);
  const [where, setWhere] = useState<Place | null>(null);

  // Typed out two characters at a time; reduced motion shows the line at once.
  useEffect(() => {
    if (!typing) return;
    const t = setInterval(() => setTyped((n) => Math.min(step.t.length, n + 2)), 11);
    return () => clearInterval(t);
  }, [typing, step.t.length]);

  useLayoutEffect(() => {
    if (target) {
      try {
        target.scrollIntoView({ block: "center", behavior: "instant" });
      } catch {
        target.scrollIntoView?.();
      }
    }
    const update = () => {
      if (panel.current) setWhere(place(panel.current.getBoundingClientRect(), target));
    };
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [target]);

  useEffect(() => {
    next.current?.focus({ preventScroll: true });
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onSkip();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onSkip]);

  // A step that asks the user to do something moves on when they do it.
  useEffect(() => {
    if (!step.wait) return;
    let t: ReturnType<typeof setTimeout> | undefined;
    const watch = (e: MouseEvent) => {
      const el = e.target as Element | null;
      if (el?.closest?.(step.wait!)) t = setTimeout(onNext, WAIT_DELAY);
    };
    document.addEventListener("click", watch, true);
    return () => {
      document.removeEventListener("click", watch, true);
      if (t) clearTimeout(t);
    };
  }, [step.wait, onNext]);

  return (
    <>
      {/* the veil only behind a centred opening step: never over the thing a step points at */}
      {i === 0 && !target && <div className="gdveil" onClick={onSkip} data-testid="guide-veil" />}
      {target && where?.ring && (
        <div
          className="gdring"
          style={{
            left: px(where.ring.left),
            top: px(where.ring.top),
            width: px(where.ring.width),
            height: px(where.ring.height),
          }}
        />
      )}
      {target && where?.arrow && (
        <div
          className={`gdarr ${where.arrow.dir}`}
          style={{ left: px(where.arrow.left), top: px(where.arrow.top) }}
        />
      )}
      <div
        className="gdp"
        ref={panel}
        role="dialog"
        aria-label="Abi’s page guide"
        data-testid="guide"
        style={
          where
            ? { left: px(where.panel.left), top: px(where.panel.top) }
            : { visibility: "hidden" }
        }
      >
        <div className="gdhead">
          <span className="gdmk" aria-hidden="true" />
          <span className="gdwho">Abi</span>
          <span className="gdstep">
            {i + 1} of {steps.length}
          </span>
        </div>
        <p className="gdt" aria-hidden="true" data-testid="guide-text">
          {step.t.slice(0, typed)}
        </p>
        <p className="gd-sr" aria-live="polite">
          {step.t}
        </p>
        {step.tryit && <span className="gdtry">Try it</span>}
        <div className="gdnav">
          <button type="button" className="gdskip" onClick={onSkip} data-testid="guide-skip">
            Skip this page
          </button>
          <button
            type="button"
            className="gdnext"
            ref={next}
            onClick={() => (typing ? setTyped(step.t.length) : onNext())}
            data-testid="guide-next"
          >
            {last ? "Got it" : "Next"}
          </button>
        </div>
      </div>
    </>
  );
}

/** "Page guide": the way back into a page's guide, on its title line. */
export function PageGuideButton({ page }: { page: GuideKey }) {
  const g = useGuides();
  if (!g) return null;
  return (
    <button
      type="button"
      className="pgguide"
      aria-label="Walk through this page with Abi again"
      onClick={() => g.start(page, true)}
      data-testid="page-guide"
    >
      <span className="gdmk2" aria-hidden="true" />
      Page guide
    </button>
  );
}
